import type { QueueItem } from "../types"
import type { DynamicCreateRequest, DynamicUpdatePayload } from "../types/dynamic"
import { createDynamicUser, updateDynamicUserByUniqueField } from "./api"
import { addToQueueDB, getQueueFromDB, removeFromQueueDB, deleteDynamicUserFromDB } from "./database"

// Callback para notificar cambios en la cola
let queueUpdateCallback: () => void = () => {}

export const setQueueUpdateCallback = (callback: () => void) => {
  queueUpdateCallback = callback
}

export const addToQueue = async (item: Omit<QueueItem, "id">): Promise<void> => {
  try {
    await addToQueueDB(item)

    console.log(`📝 Elemento agregado a la cola: ${item.type}`)

    // Notificar cambio en la cola
    queueUpdateCallback()
  } catch (error) {
    console.error("❌ Error agregando elemento a la cola:", error)
    throw error
  }
}

export const getQueueCount = async (): Promise<number> => {
  try {
    const queue = await getQueueFromDB()
    return queue.length
  } catch (error) {
    console.error("❌ Error obteniendo contador de cola:", error)
    return 0
  }
}

export const processQueue = async (): Promise<{ success: boolean; processed: number; error?: string }> => {
  try {
    const queue = await getQueueFromDB()

    if (queue.length === 0) {
      return { success: true, processed: 0 }
    }

    console.log(`🔄 Procesando ${queue.length} elementos en cola...`)

    let processed = 0
    let lastError: string | null = null

    for (const item of queue) {
      let identifier = ""

      try {
        console.log(`⏳ Procesando ${item.type}...`)

        if (item.type === "create_dynamic_user") {
          // Handle dynamic user creation
          const dynamicCreateData = item.data as DynamicCreateRequest
          identifier = `${dynamicCreateData.name} ${dynamicCreateData.last_name} (${dynamicCreateData.email})`
          const result = await createDynamicUser(dynamicCreateData)

          if (Array.isArray(result)) {
            console.log(`✅ ${result.length} usuarios dinámicos creados`)
          } else {
            console.log(`✅ Usuario dinámico creado con ID: ${result.data.id}`)
          }

          // El registro temporal (sin folio) ya no hace falta: el real llega con la siguiente actualización
          if (item.tempId) {
            await deleteDynamicUserFromDB(item.tempId).catch((err) =>
              console.warn(`⚠️ No se pudo borrar el registro temporal ${item.tempId}:`, err)
            )
          }
        } else if (item.type === "create_dynamic_user_bulk") {
          // Handle bulk dynamic user creation
          const dynamicCreateDataArray = item.data as DynamicCreateRequest[]
          identifier = `Carga masiva de ${dynamicCreateDataArray.length} usuarios`
          const result = await createDynamicUser(dynamicCreateDataArray)

          if (Array.isArray(result)) {
            console.log(`✅ ${result.length} usuarios dinámicos creados en lote`)
          } else {
            console.log(`✅ Usuarios dinámicos creados en lote`)
          }
        } else if (item.type === "update_dynamic_user") {
          // Handle dynamic user update
          const dynamicUpdateData = item.data as DynamicUpdatePayload
          identifier = `Usuario único: ${dynamicUpdateData.uniqueFieldValue}`
          await updateDynamicUserByUniqueField(dynamicUpdateData)
          console.log(`✅ Usuario dinámico actualizado: ${dynamicUpdateData.uniqueFieldValue}`)
        } else {
          // Tipo que esta versión no genera ni sabe enviar: se descarta para no atorar la cola
          console.warn(`🗑️ Operación desconocida en la cola, se descarta: ${String(item.type)}`)
          await removeFromQueueDB(item.id)
          continue
        }

        await removeFromQueueDB(item.id)
        processed++

        console.log(`✅ Procesado: ${item.type} para ${identifier}`)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Error desconocido"
        console.error(`❌ Error procesando ${item.type} para ${identifier}:`, error)

        // Verificar si es un error 207 (algunos usuarios no se pudieron crear)
        if (errorMessage.includes("207") || errorMessage.includes("Algunos usuarios no se pudieron crear")) {
          console.log(`🗑️ Error 207 detectado. Eliminando elemento de la cola para evitar reintentos: ${item.type} para ${identifier}`)
          await removeFromQueueDB(item.id)
          processed++
          // Continuar con el siguiente elemento en lugar de romper el procesamiento
          continue
        }

        // Verificar si es el error confuso "Server is running" pero que podría ser exitoso
        if (errorMessage.includes("Server is running")) {
          console.warn(`⚠️ Error confuso "Server is running" detectado para ${item.type} - ${identifier}. Verificando si la operación fue exitosa...`)
          // En este caso, podríamos considerar eliminar el elemento de la cola si creemos que fue exitoso
          // Pero por ahora, lo tratamos como un error normal para mantener la consistencia
        }

        lastError = errorMessage

        // Stop processing on first error (que no sea 207)
        break
      }
    }

    if (lastError) {
      return {
        success: false,
        processed,
        error: `Error después de procesar ${processed} elementos: ${lastError}`,
      }
    }

    console.log(`✅ Cola procesada exitosamente. ${processed} elementos procesados`)
    return { success: true, processed }
  } catch (error) {
    console.error("❌ Error general procesando cola:", error)
    return {
      success: false,
      processed: 0,
      error: error instanceof Error ? error.message : "Error desconocido",
    }
  }
}
